import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Banner } from '#pplx/frontend/aether/components/Banner/index.js';
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { Spinner } from '#pplx/frontend/aether/components/Spinner/index.js';
import { AttachmentRemoveButton } from '#pplx/frontend/apps/ask/composer-ui/attachments/AttachmentChip.js';
import { getAttachmentMediaKind } from '#pplx/frontend/apps/ask/composer-ui/attachments/attachmentMedia.js';
import { AttachmentThumbnail } from '#pplx/frontend/apps/ask/composer-ui/attachments/AttachmentThumbnail.js';
import { AttachmentTray, AttachmentTrayItem, } from '#pplx/frontend/apps/ask/composer-ui/AttachmentTray.js';
import { formatFileMetadata, getFileExtension, getIconForExtension, } from '#pplx/frontend/apps/ask/files/file-utils/fileUtils.js';
import { isImage } from '#pplx/frontend/common/files/images.js';
import { formatBytes, truncateFilenameString, } from '#pplx/frontend/components/utils/fileUploadUtils.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
const REMOVE_ATTACHMENT_TRACK_OPTIONS = {
    event: 'remove attachment clicked',
    data: {},
};
function getFileAttachmentDisplay({ file, showFileMetadata, disabled, intl, }) {
    // Resizing replaces local bytes; uploads without local bytes retain their supplied metadata.
    const hasFileBytes = file.file.size > 0;
    const fileName = showFileMetadata && hasFileBytes ? file.file.name : file.fileName;
    const fileSize = showFileMetadata && hasFileBytes ? file.file.size : file.fileSize;
    const formattedSize = showFileMetadata
        ? formatBytes(fileSize, intl)
        : formatFileMetadata({ extension: undefined, fileSize: file.fileSize, intl });
    const metadata = showFileMetadata && fileSize === 0 ? undefined : formattedSize;
    const isUploading = file.status !== 'success' && file.status !== 'failed';
    const status = !showFileMetadata || file.status === 'failed' ? getFileStatusLabel(file, intl.$t) : undefined;
    return {
        fileName,
        description: status ?? metadata ?? undefined,
        isMuted: disabled || (showFileMetadata && isUploading),
    };
}
function FileAttachmentAccessory({ file, fileName, compact, }) {
    const previewFile = file.previewTarget?.kind === 'local'
        ? file.previewTarget.file
        : compact && file.file.size > 0
            ? file.file
            : undefined;
    const kind = getAttachmentMediaKind(fileName, file.fileType);
    const extension = getFileExtension(fileName);
    const isUploading = file.status !== 'success' && file.status !== 'failed';
    return (_jsx("div", { "aria-busy": isUploading, className: compact
            ? 'flex size-full items-center justify-center overflow-hidden'
            : 'bg-soft flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg', children: _jsx(AttachmentThumbnail, { alt: fileName, kind: compact || (previewFile && isImage(previewFile)) ? kind : undefined, file: file.status === 'success' ? previewFile : undefined, fallback: isUploading ? (_jsx(Spinner, { color: "quieter", size: 16 })) : (_jsx(Icon, { color: file.status === 'failed' ? 'negative' : 'light', icon: file.status === 'failed' ? $icon('alert-triangle') : getIconForExtension(extension), size: "small" })) }) }));
}
/** Renders attachment upload status and collection errors in the Composer tray slot. */
export function FileAttachmentTray({ showFileMetadata = false, presentation = 'default', disabled, errorMessage, files, onDismissError, onPreviewAttachment, onRemoveFile, }) {
    const intl = useIntl();
    const { $t } = intl;
    const { track } = useInstrumentation();
    const handleRemoveFile = useCallback((id) => {
        track(REMOVE_ATTACHMENT_TRACK_OPTIONS);
        onRemoveFile(id);
    }, [onRemoveFile, track]);
    if (files.length === 0 && errorMessage === undefined)
        return null;
    const showMedia = presentation === 'media' &&
        files.every((file) => file.status !== 'failed' && getAttachmentMediaKind(file.fileName, file.fileType));
    return (_jsxs("div", { className: "flex min-w-0 flex-col gap-2", children: [errorMessage !== undefined ? (_jsx("div", { className: "px-4", children: _jsx(Banner, { leadingAccessory: $icon('alert-triangle'), size: "compact", title: errorMessage, variant: "error", onClose: onDismissError }) })) : null, files.length > 0 ? (_jsx(AttachmentTray, { layout: presentation === 'default' ? 'scroll' : 'wrap', children: files.map((file) => {
                    const { fileName, description, isMuted } = getFileAttachmentDisplay({
                        file,
                        showFileMetadata,
                        disabled,
                        intl,
                    });
                    const previewLabel = $t({
                        defaultMessage: 'Preview {fileName}',
                        id: '7952fdb885',
                        description: 'Accessible label for previewing a Composer attachment',
                    }, { fileName });
                    const removeLabel = $t({
                        defaultMessage: 'Remove {fileName}',
                        id: 'c448741142',
                        description: 'Accessible label for removing a teammate task attachment',
                    }, { fileName });
                    const itemProps = {
                        presentation: showMedia
                            ? 'media'
                            : presentation === 'default'
                                ? 'default'
                                : 'compact',
                        description,
                        isError: file.status === 'failed',
                        isMuted,
                        label: truncateFilenameString(fileName, presentation === 'default' ? 30 : 24),
                        leadingAccessory: (_jsx(FileAttachmentAccessory, { file: file, fileName: fileName, compact: presentation !== 'default' })),
                        title: file.error ?? fileName,
                        trailingAccessory: disabled ? null : presentation === 'default' ? (_jsx(Button, { "aria-label": removeLabel, icon: $icon('x'), size: "tiny", variant: "text", rounded: true, onClick: () => handleRemoveFile(file.id) })) : (_jsx(AttachmentRemoveButton, { label: removeLabel, media: showMedia, onClick: () => handleRemoveFile(file.id) })),
                    };
                    if (onPreviewAttachment === undefined) {
                        return _jsx(AttachmentTrayItem, { ...itemProps }, file.id);
                    }
                    return (_jsx(AttachmentTrayItem, { ...itemProps, activationAriaLabel: previewLabel, disabled: file.disabled, onActivate: () => {
                            if (file.previewTarget !== undefined)
                                onPreviewAttachment(file.previewTarget);
                        } }, file.id));
                }) })) : null] }));
}
function getFileStatusLabel(file, $t) {
    if (file.status === 'failed') {
        return $t({
            defaultMessage: 'Upload failed',
            id: '63954302ee',
            description: 'Status shown on a teammate task attachment that failed to upload',
        });
    }
    if (file.status !== 'success') {
        return $t({
            defaultMessage: 'Uploading...',
            id: 'bd490d614c',
            description: 'Status shown while a teammate task attachment is uploading',
        });
    }
    return undefined;
}
//# sourceMappingURL=FileAttachmentTray.js.map